import { useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';
import { useTranslation } from 'react-i18next';

import { useUploadStore } from '@/store/uploadStore';
import { UploadQueueItem } from './UploadQueueItem';
import type { UploadStatus } from '@/upload/uploadTypes';

// Row height in pixels
const ROW_HEIGHT = 64;
// Max visible height before scrolling
const MAX_LIST_HEIGHT = 520;

/** Priority order for sorting items by status group. */
const STATUS_ORDER: Record<UploadStatus, number> = {
  uploading: 0,
  hashing: 1,
  registering: 2,
  completing: 3,
  pending: 4,
  failed: 5,
  completed: 6,
};

export function UploadQueue() {
  const { t } = useTranslation();

  // Subscribe to the full queue (we only read ids + status for sorting).
  const queue = useUploadStore((s) => s.queue);

  // Sorted ids for the virtualized list -- uploading first, then pending, failed, completed.
  const sortedIds = useMemo(() => {
    const entries = queue.map((item) => ({
      id: item.id,
      order: STATUS_ORDER[item.status],
    }));
    entries.sort((a, b) => a.order - b.order);
    return entries.map((e) => e.id);
  }, [queue]);

  const listHeight = Math.min(sortedIds.length * ROW_HEIGHT, MAX_LIST_HEIGHT);

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const id = sortedIds[index];
      return <UploadQueueItem key={id} id={id} style={style} />;
    },
    [sortedIds],
  );

  if (queue.length === 0) {
    return null;
  }

  // Summary counts
  const uploading = queue.filter(
    (i) =>
      i.status === 'uploading' ||
      i.status === 'hashing' ||
      i.status === 'registering' ||
      i.status === 'completing',
  ).length;
  const pending = queue.filter((i) => i.status === 'pending').length;
  const completed = queue.filter((i) => i.status === 'completed').length;
  const failed = queue.filter((i) => i.status === 'failed').length;

  return (
    <div className="card overflow-hidden">
      {/* Mini status bar */}
      <div className="flex items-center gap-4 border-b border-gray-100 px-4 py-2.5 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          {t('upload.queueTitle', { count: queue.length })}
        </span>
        {uploading > 0 && (
          <span className="text-primary-600 dark:text-primary-400">
            {t('upload.activeCount', { count: uploading })}
          </span>
        )}
        {pending > 0 && (
          <span>{t('upload.pendingCount', { count: pending })}</span>
        )}
        {completed > 0 && (
          <span className="text-green-600 dark:text-green-400">
            {t('upload.completedCount', { count: completed })}
          </span>
        )}
        {failed > 0 && (
          <span className="text-red-600 dark:text-red-400">
            {t('upload.failedQueueCount', { count: failed })}
          </span>
        )}
      </div>

      {/* Virtualized list */}
      <List
        height={listHeight}
        itemCount={sortedIds.length}
        itemSize={ROW_HEIGHT}
        width="100%"
        overscanCount={10}
      >
        {Row}
      </List>
    </div>
  );
}
